import { LogOut, Lock } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import Logo from "../../assets/logo.webp";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { Eye, EyeOff, X, CheckCircle } from "lucide-react";
import api from "../../config/configApi";

// Importar o sistema de temas
import { 
  getCurrentTheme, 
  getCampaignText, 
  isThemedMonth, 
  ThemeConfig 
} from '../../config/themeConfig';

// Componente Modal para Alteração de Senha
function ModalAlterarSenha({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: string;
}) {
  const [senha, setSenha] = useState("");
  const [confirmaSenha, setConfirmaSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmaSenha, setMostrarConfirmaSenha] = useState(false);
  const [validaSenha, setValidaSenha] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [loading, ] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const DsEspecialidade = Cookies.get("dsEspecialidade");

  // Usar o sistema de temas importado
  const currentTheme: ThemeConfig = getCurrentTheme();
  const campaign = getCampaignText(currentTheme);
  const isThemed = isThemedMonth();

  // Configurações de cores baseadas no tema
  const getThemeConfig = () => {
    switch (currentTheme.theme) {
      case 'pink':
        return {
          primaryColor: 'bg-pink-600',
          hoverColor: 'hover:bg-pink-700',
          accentColor: 'text-pink-600',
          lightBg: 'bg-pink-50',
          borderColor: 'border-pink-100',
          textColor: 'text-pink-800'
        };
      case 'blue':
        return {
          primaryColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          accentColor: 'text-blue-600',
          lightBg: 'bg-blue-50',
          borderColor: 'border-blue-100',
          textColor: 'text-blue-800'
        };
      case 'yellow':
        return {
          primaryColor: 'bg-yellow-600',
          hoverColor: 'hover:bg-yellow-700',
          accentColor: 'text-yellow-600',
          lightBg: 'bg-yellow-50',
          borderColor: 'border-yellow-100',
          textColor: 'text-yellow-800'
        };
      case 'red':
        return {
          primaryColor: 'bg-red-600',
          hoverColor: 'hover:bg-red-700',
          accentColor: 'text-red-600',
          lightBg: 'bg-red-50',
          borderColor: 'border-red-100',
          textColor: 'text-red-800'
        };
      default:
        return {
          primaryColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          accentColor: 'text-blue-600',
          lightBg: 'bg-blue-50',
          borderColor: 'border-blue-100',
          textColor: 'text-blue-800'
        };
    }
  };

  const themeConfig = getThemeConfig();

  
 const handleClose = useCallback(() => {
  setSenha("");
  setConfirmaSenha("");
  onClose();
}, [onClose]); // Add onClose as dependency

  // Fechar o modal ao clicar fora dele
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(event.target as Node)
    ) {
      handleClose();
    }
  };

  if (isOpen) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [isOpen, handleClose]); // Add handleClose to dependencies

  // Função para validar a senha em tempo real
  const validarSenha = (senha: string) => {
    setValidaSenha({
      minLength: senha.length >= 8,
      hasUpperCase: /[A-Z]/.test(senha),
      hasLowerCase: /[a-z]/.test(senha),
      hasNumber: /[0-9]/.test(senha),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(senha),
    });
  };

  const handleChangeSenha = (e: React.ChangeEvent<HTMLInputElement>) => {
    const novaSenha = e.target.value;
    setSenha(novaSenha);
    validarSenha(novaSenha);
  };

  // Função para enviar alteração de senha
  const handleEnviarSenha = async () => {
    const user = Cookies.get("User");

    if (!user) {
      Swal.fire("Erro", "Usuário não identificado.", "error");
      return;
    }

    if (!senha || !confirmaSenha) {
      Swal.fire("Erro", "Preencha os dois campos de senha.", "error");
      return;
    }

    if (senha !== confirmaSenha) {
      Swal.fire("Erro", "As senhas não coincidem.", "error");
      return;
    }

    const regrasSenhaValidas = Object.values(validaSenha).every(
      (regra) => regra
    );
    if (!regrasSenhaValidas) {
      Swal.fire("Erro", "A senha não atende todos os critérios.", "error");
      return;
    }

    try {
      // Codificar o nome do usuário para URL
      const usuarioCodificado = encodeURIComponent(user);

      const response = await api.patch(
        `/usuarios/${usuarioCodificado}`,
        {
          senha: senha,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        Swal.fire("Sucesso", "Senha alterada com sucesso!", "success");
        setSenha("");
        setConfirmaSenha("");
        onClose();
      } else {
        Swal.fire("Erro", "Não foi possível alterar a senha.", "error");
      }
    } catch (error) {
      console.error("Erro detalhado:", error);

      let mensagemErro = "Erro ao alterar senha.";
      
      // Verificar se é um erro do Axios
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        
        if (axiosError.response?.data?.message) {
          mensagemErro = axiosError.response.data.message;
        } else if (axiosError.response?.status === 500) {
          mensagemErro =
            "Erro interno do servidor. Verifique os logs do servidor.";
        } else if (axiosError.code === "NETWORK_ERROR") {
          mensagemErro = "Erro de conexão. Verifique sua internet.";
        }
      } else if (error instanceof Error) {
        mensagemErro = error.message;
      }
      
      Swal.fire("Erro", mensagemErro, "error");
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">Alterar Senha</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6">
          {/* Banner da campanha no modal */}
          {isThemed && (
            <div className={`${themeConfig.primaryColor} text-white text-center py-2 rounded-lg mb-4 `}>
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">{currentTheme.decorations.emoji}</span>
                <div>
                  <h3 className="font-bold text-sm">{campaign.title}</h3>
                  <p className="text-xs opacity-90">{campaign.subtitle}</p>
                </div>
                <span className="text-lg">{currentTheme.decorations.emoji}</span>
              </div>
            </div>
          )}

          {/* Seção de informações do usuário */}
          <div className={`mb-4 md:mb-6 p-3 md:p-4 ${themeConfig.lightBg} rounded-lg border ${themeConfig.borderColor}`}>
            <h3 className={`text-xs font-semibold ${themeConfig.textColor} mb-2`}>
              Usuário
            </h3>
            <div className="flex items-center">
              <div className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full ${
                currentTheme.theme === 'pink' ? 'bg-pink-200' : 
                currentTheme.theme === 'blue' ? 'bg-blue-200' :
                currentTheme.theme === 'yellow' ? 'bg-yellow-200' :
                currentTheme.theme === 'red' ? 'bg-red-200' : 'bg-blue-200'
              } mr-3`}>
                <span className={`text-sm md:text-lg font-bold ${
                  currentTheme.theme === 'pink' ? 'text-pink-700' : 
                  currentTheme.theme === 'blue' ? 'text-blue-700' :
                  currentTheme.theme === 'yellow' ? 'text-yellow-700' :
                  currentTheme.theme === 'red' ? 'text-red-700' : 'text-blue-700'
                }`}>
                  {user ? user.charAt(0).toUpperCase() : ""}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-800 text-xs md:text-sm">{user}</p>
                <p className={`font-medium ${themeConfig.accentColor} text-xs md:text-sm`}>{DsEspecialidade}</p>
                <p className="text-xs text-gray-900">
                  Alterando senha de acesso
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-xs font-bold mb-2">
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={mostrarSenha ? "text" : "password"}
                value={senha}
                onChange={handleChangeSenha}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                placeholder="Digite sua nova senha"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
              >
                {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="mb-4 md:mb-6">
            <label className="block text-gray-700 text-xs font-bold mb-2">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={mostrarConfirmaSenha ? "text" : "password"}
                value={confirmaSenha}
                onChange={(e) => setConfirmaSenha(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                placeholder="Confirme sua nova senha"
              />
              <button
                type="button"
                onClick={() => setMostrarConfirmaSenha(!mostrarConfirmaSenha)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
              >
                {mostrarConfirmaSenha ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          <div className="mb-4 md:mb-6 bg-gray-50 p-3 rounded-md">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">
              A senha deve conter:
            </h3>
            <ul className="text-xs md:text-sm text-gray-800 space-y-1">
              <li className="flex items-center">
                <CheckCircle
                  size={14}
                  className={`mr-2 ${
                    validaSenha.minLength ? "text-green-500" : "text-gray-400"
                  }`}
                />
                Pelo menos 8 caracteres
              </li>
              <li className="flex items-center">
                <CheckCircle
                  size={14}
                  className={`mr-2 ${
                    validaSenha.hasUpperCase
                      ? "text-green-500"
                      : "text-gray-400"
                  }`}
                />
                Pelo menos uma letra maiúscula
              </li>
              <li className="flex items-center">
                <CheckCircle
                  size={14}
                  className={`mr-2 ${
                    validaSenha.hasLowerCase
                      ? "text-green-500"
                      : "text-gray-400"
                  }`}
                />
                Pelo menos uma letra minúscula
              </li>
              <li className="flex items-center">
                <CheckCircle
                  size={14}
                  className={`mr-2 ${
                    validaSenha.hasNumber ? "text-green-500" : "text-gray-400"
                  }`}
                />
                Pelo menos um número
              </li>
              <li className="flex items-center">
                <CheckCircle
                  size={14}
                  className={`mr-2 ${
                    validaSenha.hasSpecialChar
                      ? "text-green-500"
                      : "text-gray-400"
                  }`}
                />
                Pelo menos um caractere especial
              </li>
            </ul>
          </div>

         <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition mt-2 sm:mt-0 flex-1 sm:flex-none"
            >
              Cancelar
            </button>
            <button
              onClick={handleEnviarSenha}
              disabled={
                loading ||
                !Object.values(validaSenha).every((regra) => regra) ||
                senha !== confirmaSenha
              }
              className={`px-4 py-2 ${themeConfig.primaryColor} text-black rounded-lg ${themeConfig.hoverColor} transition font-semibold shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed flex-1 sm:flex-none`}
            >
              {loading ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Componente Menu Principal
export default function Menu({
  onSelectMenu,
}: {
  onSelectMenu: (path: string) => void;
}) {
  const [modalSenhaAberto, setModalSenhaAberto] = useState(false);

  const DsEspecialidade = Cookies.get("dsEspecialidade");
  const user = Cookies.get("nmProfissional");
  const navigate = useNavigate();

  // Usar o sistema de temas importado
  const currentTheme: ThemeConfig = getCurrentTheme();
  const campaign = getCampaignText(currentTheme);
  const isThemed = isThemedMonth();

  // Configurações de cores baseadas no tema
  const getThemeConfig = () => {
    switch (currentTheme.theme) {
      case 'pink':
        return {
          primaryColor: 'bg-pink-600',
          hoverColor: 'hover:bg-pink-700',
          accentColor: 'text-pink-600',
          lightBg: 'bg-pink-50',
          borderColor: 'border-pink-200',
          textColor: 'text-pink-800'
        };
      case 'blue':
        return {
          primaryColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          accentColor: 'text-blue-600',
          lightBg: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800'
        };
      case 'yellow':
        return {
          primaryColor: 'bg-yellow-600',
          hoverColor: 'hover:bg-yellow-700',
          accentColor: 'text-yellow-600',
          lightBg: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800'
        };
      case 'red':
        return {
          primaryColor: 'bg-red-600',
          hoverColor: 'hover:bg-red-700',
          accentColor: 'text-red-600',
          lightBg: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800'
        };
      default:
        return {
          primaryColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          accentColor: 'text-blue-600',
          lightBg: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800'
        };
    }
  };

  const themeConfig = getThemeConfig();

  const formatElapsedTime = (loginTimestamp: number) => {
    const now = new Date();
    const loginTime = new Date(loginTimestamp);
    const diffMs = now.getTime() - loginTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `Logado há ${diffMins} ${diffMins === 1 ? "minuto" : "minutos"}`;
    } else {
      const hours = loginTime.getHours().toString().padStart(2, "0");
      const minutes = loginTime.getMinutes().toString().padStart(2, "0");
      return `Logado às ${hours}:${minutes} até o momento`;
    }
  };

  const handleLogout = async () => {
    [
      "Frontend",
      "User",
      "nmProfissional",
      "loginTime",
      "idProfissional",
      "idEvent",
      "cdPaciente",
      "Nome",
      "NuCPF",
      "cpf",
      "dsEspecialidade",
      "idEspecialidade",
      "flPms",
      "Role",
      "idade",
      "CRM",
      "cboTuss",
      "CartaoSUS",
      "NumeroDacarteirinha",
      "nmEndereco",
      "nuEndereco",
      "nmComplEndereco",
      "nmBairroEndereco",
      "nmCidadeEndereco",
      "nmUfEndereco",
      "atendimentoConcluido",
      "atendimentoNutricionistaConcluido",
      "loginConfirmed",
    ].forEach((item) => Cookies.remove(item));
    sessionStorage.removeItem("token");

    Swal.fire({
      title: "Confirmado!",
      text: "Você foi desconectado com sucesso!",
      icon: "success",
      showConfirmButton: false,
      timer: 1700,
      timerProgressBar: true,
      willClose: () => navigate("/"),
    });
  };

  return (
    <>
      <header className="bg-white-800 text-black flex flex-col sm:flex-row justify-between items-center px-4 sm:px-6 py-2 gap-4">
        <div className="flex items-center">
          <img src={Logo} alt="Logo" className="h-8 md:h-10" />
          {/* Banner da campanha ao lado do logo */}
          {isThemed && (
            <div className={`ml-4 px-3 py-1 rounded-full ${themeConfig.primaryColor} text-white text-xs font-semibold flex items-center gap-2 animate-pulse`}>
              <span>{currentTheme.decorations.emoji}</span>
              <span>{campaign.title}</span>
              <span>{currentTheme.decorations.emoji}</span>
            </div>
          )}
        </div>

        <div className="w-full sm:w-auto">
          <div className={`border px-3 py-2 md:px-5 md:py-2 rounded-full shadow flex flex-col items-center gap-1 md:gap-2 ${isThemed ? themeConfig.borderColor : 'border-gray-200'}`}>
            {/* Container com usuário e especialidade */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Avatar ou inicial do usuário */}
              <div className={`w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full ${
                isThemed ? (
                  currentTheme.theme === 'pink' ? 'bg-pink-100' : 
                  currentTheme.theme === 'blue' ? 'bg-blue-100' :
                  currentTheme.theme === 'yellow' ? 'bg-yellow-100' :
                  currentTheme.theme === 'red' ? 'bg-red-100' : 'bg-blue-100'
                ) : 'bg-blue-100'
              }`}>
                <span className={`text-xs md:text-sm font-bold ${
                  isThemed ? (
                    currentTheme.theme === 'pink' ? 'text-pink-700' : 
                    currentTheme.theme === 'blue' ? 'text-blue-700' :
                    currentTheme.theme === 'yellow' ? 'text-yellow-700' :
                    currentTheme.theme === 'red' ? 'text-red-700' : 'text-blue-700'
                  ) : 'text-blue-700'
                }`}>
                  {user ? user.charAt(0).toUpperCase() : ""}
                </span>
              </div>

              {/* Informações do usuário */}
              <div className="flex flex-col leading-tight text-left">
                <span className={`text-xs font-semibold ${isThemed ? themeConfig.textColor : 'text-blue-800'}`}>
                  {user ? user.charAt(0).toUpperCase() + user.slice(1) : ""}
                </span>

                <span className="text-xs text-gray-700">
                  Especialidade:{" "}
                  <span className="font-bold text-black">
                    {DsEspecialidade}
                  </span>
                </span>
              </div>
            </div>

            {/* Tempo logado centralizado */}
            <div className="text-[10px] md:text-[11px] text-red-500 italic text-center">
              {formatElapsedTime(Number(Cookies.get("loginTime")))}
            </div>
          </div>
        </div>

        {/* Botões à direita */}
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => setModalSenhaAberto(true)}
            className={`${isThemed ? themeConfig.accentColor : 'text-black'} hover:${
              isThemed ? (
                currentTheme.theme === 'pink' ? 'text-pink-500' : 
                currentTheme.theme === 'blue' ? 'text-blue-500' :
                currentTheme.theme === 'yellow' ? 'text-yellow-500' :
                currentTheme.theme === 'red' ? 'text-red-500' : 'text-blue-500'
              ) : 'text-blue-500'
            } flex items-center gap-1 text-sm md:text-base`}
          >
            <Lock size={16} /> Alterar Senha
          </button>
          <button
            onClick={handleLogout}
            className="text-red-600 hover:hover:text-red-400 flex items-center gap-1 text-sm md:text-base"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      </header>

      <div className="flex flex-col bg-gray-100">
        <nav className={`${isThemed ? themeConfig.primaryColor : 'bg-blue-700'} text-white px-4 md:px-6 py-2 md:py-3`}>
          <div className="flex gap-4 md:gap-6 text-sm font-medium items-center"></div>
        </nav>
      </div>

      {/* Modal de Alteração de Senha */}
      <ModalAlterarSenha
        isOpen={modalSenhaAberto}
        onClose={() => setModalSenhaAberto(false)}
        user={user || ""}
      />
    </>
  );
}